import { Injectable } from '@angular/core';
import { HttpLink } from 'apollo-angular/http';
import { Observable, of } from 'rxjs';
import {
  concatMap, map, take
} from 'rxjs/operators';

import {
  AccountInvitationInput,
  MutationChangePasswordArgs,
  AccountRolesOfAccountCreateInput,
  AccountUpdateInput,
  BranchRolesOfAccountCreateInput,
  Role,
  Account,
  BranchRoleMapping,
  Branch,
  AccountType,
  AccountPreferences,
  AccountPreferencesWhereInput,
  AccountPreferencesType,
  AccountPreferencesCreateInput,
  AccountUpdateWithoutAccountRolesInput
} from 'graphql/models/generated';
import { ActivateAccountMutation } from 'graphql/mutations/activate-account';
import { DeactivateAccountMutation } from 'graphql/mutations/deactivate-account';
import { DeleteAccountMutation } from 'graphql/mutations/delete-account';
import { InviteAccountMutation } from 'graphql/mutations/invite-account';
import { LogoutMutation } from 'graphql/mutations/logout';
import { UpdateAccountPasswordMutation } from 'graphql/mutations/update-account-password';
import { ReinviteAccountMutation } from 'graphql/mutations/reinvite-account';
import { SetAccountPreferenceMutation } from 'graphql/mutations/set-account-preference';
import { AccountPreferencesQuery } from 'graphql/queries/account-preferences';
import {
  AccountStatus, AccountTypeENUM
} from 'models/account.model';
import { AccountPreferencesNameENUM } from 'models/account-preferences.model';
import { DropdownItem } from 'models/dropdown.model';
import { CoreDataBuilderService } from 'shared/services/core-data-builder.service';
import { MediaService } from 'shared/services/media.service';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  constructor(
    private builderService: CoreDataBuilderService,
    private mediaService: MediaService,
    private activateAccountMutation: ActivateAccountMutation,
    private accountPreferencesQuery: AccountPreferencesQuery,
    private deactivateAccountMutation: DeactivateAccountMutation,
    private deleteAccountMutation: DeleteAccountMutation,
    private updateAccountPasswordMutation: UpdateAccountPasswordMutation,
    private inviteAccountMutation: InviteAccountMutation,
    private reinviteAccountMutation: ReinviteAccountMutation,
    private setAccountPreferenceMutation: SetAccountPreferenceMutation,
    private logoutMutation: LogoutMutation,
    protected httpLink: HttpLink
  ) { }

  getAccount(id: string): Observable<Account> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Account>('Account', variables, 'AccountBaseFragment');
  }

  getAccountBasic(username: string): Observable<Account> {
    const where = { username };
    const variables = { where };

    return this.builderService.getOne<Account>('Account', variables, 'AccountBasicFragment');
  }

  getAccountBranches(id: string): Observable<Branch[] | undefined> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Account>('Account', variables, 'AccountBranchesFragment')
      .pipe<Branch[] | undefined>(
        map((r) => r.branches)
      );
  }

  getAccountProfile(id?: string, username?: string): Observable<Account> {
    const where = { id, username };
    const variables = { where };

    return this.builderService.getOne<Account>('Account', variables, 'AccountProfileFragment');
  }

  getAccountProfilePhoto(id: string): Observable<Account> {
    const where = { id };
    const variables = { where };

    return this.builderService.getOne<Account>('Account', variables, 'AccountProfileImageFragment');
  }

  updateAccountProfileWithPhoto(
    accountId: string,
    formValue: Record<string, any>,
    uploadUrl?: string
  ): Observable<Account> {
    const formsValue = { ...formValue };
    const photo: File = formsValue.photo;
    delete formsValue.photo;

    return this.updateAccountProfile(accountId, formsValue)
      .pipe(
        concatMap((account: Account): Observable<Account> => {
          if (photo && uploadUrl) {
            return this.mediaService.uploadFile(uploadUrl, photo)
              .pipe(map(() => account));
          }

          return of(account);
        })
      );
  }

  updateAccountProfile(id: string, formValue: Record<string, any>): Observable<Account> {
    const updateInput = this.builderService.buildDataInput<Record<string, any>>(formValue, false);
    const data: AccountUpdateInput = { profile: { update: updateInput } };

    return this.builderService.update<Account, AccountUpdateInput>('Account', id, data, 'AccountProfileFragment');
  }

  createAccountProfileWithPhoto(
    accountId: string,
    formValue: Record<string, any>,
    uploadUrl?: string
  ): Observable<Account> {
    const formsValue = { ...formValue };
    const photo: File = formsValue.photo;
    delete formsValue.photo;

    return this.createAccountProfile(accountId, formsValue)
      .pipe(
        concatMap((account: Account): Observable<Account> => {
          if (photo && uploadUrl) {
            return this.mediaService.uploadFile(uploadUrl, photo)
              .pipe(map(() => account));
          }

          return of(account);
        })
      );
  }

  createAccountProfile(
    id: string,
    formValue: Record<string, any>,
    fragment = 'AccountProfileFragment'
  ): Observable<Account> {
    const data: AccountUpdateInput = { profile: { create: { ...formValue } } };

    return this.builderService.update<Account, AccountUpdateInput>('Account', id, data, fragment);
  }

  updateTeamDetails(id: string, branchRoles: BranchRoleMapping[], rfpShow?: boolean): Observable<Account> {
    let data: AccountUpdateInput = { accountRoles: { branchRoles } };

    return this.builderService.update<Account, AccountUpdateInput>('Account', id, data, 'AccountBaseFragment')
    .pipe(
      concatMap(() => {
        if (rfpShow !== undefined) {
          return this.updateAccountRfpSettings(id, { rfpShow: rfpShow as any } as AccountUpdateWithoutAccountRolesInput)
        }

        return this.getAccount(id)
      })
    );
  }

  updateAccountRoles(id: string, branchRoles: BranchRoleMapping[]): Observable<Account> {
    const data: AccountUpdateInput = { accountRoles: { branchRoles } };

    return this.builderService.update<Account, AccountUpdateInput>('Account', id, data, 'AccountBaseFragment')
      .pipe(concatMap(() => this.getAccount(id)));
  }

  updateAccountRfpSettings(id: string, data: AccountUpdateWithoutAccountRolesInput): Observable<Account> {

    return this.builderService.update<Account, AccountUpdateWithoutAccountRolesInput>('Account', id, data, 'AccountBaseFragment')
    .pipe(concatMap(() => this.getAccount(id)));
  }

  activateAccount(id: string): Observable<Account> {
    return this.activateAccountMutation.mutate({ id }, { fetchPolicy: 'no-cache' })
      .pipe<unknown, Account, Account>(
        map((r) => r.data?.activateAccount),
        concatMap(() => this.getAccount(id)),
        take(1)
      );
  }

  inviteAgain(username: string,): Observable<any> {
    return this.reinviteAccountMutation.mutate({ username }, { fetchPolicy: 'no-cache' })
      .pipe<AccountStatus, AccountStatus>(
        map((r) => (r.data as any)?.inviteAccount?.status),
        take(1)
      );
  }

  deactivateAccount(id: string): Observable<Account> {
    return this.deactivateAccountMutation.mutate({ id }, { fetchPolicy: 'no-cache' })
      .pipe<unknown, Account, Account>(
        map((r) => r.data?.deactivateAccount),
        concatMap(() => this.getAccount(id)),
        take(1)
      );
  }

  deleteAccount(id: string): Observable<any> {
    return this.deleteAccountMutation.mutate({ id }, { fetchPolicy: 'no-cache' })
      .pipe<unknown>(
        take(1)
      );
  }

  inviteAccount(username: string, branchesIds: string[], role: Role, accountType: AccountType, orgId?: string): Observable<any> {
    const branchRoles: BranchRolesOfAccountCreateInput[] = branchesIds
      .map<BranchRolesOfAccountCreateInput>((branch): BranchRolesOfAccountCreateInput => ({
        branchId: branch,
        role
      }));

    const createsSuperAdmin = Boolean(orgId);

    const accountRoles: AccountRolesOfAccountCreateInput = {
      branchRoles,
      organizationRole: createsSuperAdmin ? 'SUPERADMIN' : role
    };

    const accountInvitationInput: AccountInvitationInput = {
      username,
      accountType,
      accountRoles,
      organization: orgId
    };

    return this.inviteAccountMutation.mutate({ accountInvitationInput }, { fetchPolicy: 'no-cache' })
      .pipe<AccountStatus, AccountStatus>(
        map((r) => (r.data as any)?.inviteAccount?.status),
        take(1)
      );
  }

  updateAccountPassword(formValue: Record<string, any>): Observable<boolean> {
    const variables: MutationChangePasswordArgs = {
      currentPassword: formValue.currentPassword,
      newPassword: formValue.password.newPassword,
      confirmation: formValue.password.confirmPassword
    };

    return this.updateAccountPasswordMutation.mutate(variables, { fetchPolicy: 'no-cache' })
      .pipe<boolean, boolean>(
        map((r) => r.data?.changePassword as boolean),
        take(1)
      );
  }

  getAccountPreferences(
    accountId: string,
    type?: AccountPreferencesType,
    key?: string,
    name?: AccountPreferencesNameENUM
  ): Observable<AccountPreferences[]> {
    const where: AccountPreferencesWhereInput = {
      accountId,
      type,
      key,
      name
    };

    const variables = { where };

    return this.accountPreferencesQuery.fetch(variables, { fetchPolicy: 'no-cache' })
    .pipe<AccountPreferences[], AccountPreferences[]>(
      map((r) => r.data?.accountPreferences as AccountPreferences[]),
      take(1)
    );
  }

  setAccountPreference(
    accountId: string,
    type: AccountPreferencesType,
    key: string,
    name: AccountPreferencesNameENUM,
    value: string
  ): Observable<AccountPreferences> {
    const variables: AccountPreferencesCreateInput = {
      accountId,
      type,
      key,
      name,
      value
    };

    const data = { data: variables };

    return this.setAccountPreferenceMutation.mutate(data, { fetchPolicy: 'no-cache' })
    .pipe<AccountPreferences, AccountPreferences>(
      map((r) => r.data?.setAccountPreference as AccountPreferences),
      take(1)
    );
  }

  getManufacturerAccountsForRFP(): Observable<Account[]> {
    const variables = {
      where: {
        "AND": [
          {
            "accountType": {
              "equals": AccountTypeENUM.MANUFACTURER
            }
          },
          {
            "rfpShow": {
              "equals": true
            }
          }
        ]
      }
    };

    return this.builderService.getMany<Account>('Account', variables, 'AccountBaseFragment');
  }

  getDealerAccounts(branchId: string): Observable<Account[]> {
    const variables = {
      where: {
        accountType: { equals: AccountTypeENUM.DEALER },
        accountRoles: {
          branchRoles: { every: { branchId: { equals: branchId } } }
        }
      }
    };

    return this.builderService.getMany<Account>('Account', variables, 'AccountBaseFragment');
  }

  mapAccountsToDropdown(accounts: Account[]): DropdownItem[] {
    return accounts.map((acc) => (
      (acc.id && acc.username && acc.profile && acc.profile.firstName && acc.profile.lastName)
        ? {
          title: `${acc.profile.firstName} ${acc.profile.lastName}`,
          value: acc.username
        } : { title: '', value: '' }
    )).filter((acc) => acc.title && acc.value);
  }

  logout(): Observable<any> {
    return this.logoutMutation.mutate()
      .pipe<boolean, boolean>(
        map((r) => r.data?.logout as boolean),
        take(1)
      );
  }
}
